import { Circle, MousePointer2, PenTool, Scan, Square } from 'lucide-react';
import type { ReactElement } from 'react';
import { IconToolButton } from '@/shared/ui';

/** 캔버스가 쥐는 도형 도구. 자유 다각형은 스토어의 그리기 상태라 여기 없다. */
export type DrawTool = 'select' | 'rect' | 'ellipse';

interface DrawToolBarProps {
  /**
   * 'area'(기본)는 인쇄 영역 도구 줄(선택·사각형·원형·자유 다각형·전체 영역), 'hole' 은 인쇄 제외 영역 도구 줄
   * (사각형·원형·자유 다각형만)이다. 두 줄은 같은 버튼·같은 규칙을 쓴다.
   */
  readonly variant?: 'area' | 'hole';
  /** 눌린 것으로 보일 도구. 아무것도 켜지지 않았으면 null. */
  readonly pressedTool: DrawTool | 'polygon' | null;
  /** 사각형·원형을 쓸 수 없는가. */
  readonly rectDisabled: boolean;
  readonly polygonDisabled: boolean;
  /** 버튼이 꺼진 사유(마우스를 올리면 보인다). 줄로 보일 사유는 notes 로 따로 받는다. */
  readonly rectTitle: string | undefined;
  readonly polygonTitle: string | undefined;
  /** 도구 줄 아래에 보일 안내(꺼진 사유). */
  readonly notes: readonly string[];
  /** 전체 영역 버튼('area' 줄에만 있다). */
  readonly fullAreaDisabled?: boolean;
  readonly fullAreaTitle?: string | undefined;
  /** 전체 영역 실행이 거부된 사유. */
  readonly fullAreaError?: string | null;
  readonly onTool: (tool: DrawTool) => void;
  readonly onPolygon: () => void;
  readonly onFullArea?: () => void;
}

/**
 * 사진 위 도형 도구 줄. 'area' 는 선택·사각형·원형·자유 다각형·전체 영역, 'hole' 은 사각형·원형·자유 다각형이다.
 * 한 번에 하나만 켜진다.
 */
export const DrawToolBar = ({
  variant = 'area',
  pressedTool,
  rectDisabled,
  polygonDisabled,
  rectTitle,
  polygonTitle,
  notes,
  fullAreaDisabled = true,
  fullAreaTitle,
  fullAreaError = null,
  onTool,
  onPolygon,
  onFullArea,
}: DrawToolBarProps): ReactElement => {
  const area = variant === 'area';
  return (
    <div className="space-y-1">
      <div
        role="toolbar"
        aria-label={area ? '도형 도구' : '인쇄 제외 영역 도구'}
        className="flex flex-wrap items-center gap-1"
      >
        {area ? (
          <IconToolButton
            icon={MousePointer2}
            label="선택"
            pressed={pressedTool === 'select'}
            onClick={() => onTool('select')}
          />
        ) : null}
        <IconToolButton
          icon={Square}
          label="사각형"
          pressed={pressedTool === 'rect'}
          disabled={rectDisabled}
          title={rectTitle}
          onClick={() => onTool('rect')}
        />
        <IconToolButton
          icon={Circle}
          label="원형"
          pressed={pressedTool === 'ellipse'}
          disabled={rectDisabled}
          title={rectTitle}
          onClick={() => onTool('ellipse')}
        />
        <IconToolButton
          icon={PenTool}
          label="자유 다각형"
          pressed={pressedTool === 'polygon'}
          disabled={polygonDisabled}
          title={polygonTitle}
          onClick={onPolygon}
        />
        {area ? (
          <IconToolButton
            icon={Scan}
            label="전체 영역"
            disabled={fullAreaDisabled}
            title={fullAreaTitle}
            onClick={onFullArea}
          />
        ) : null}
      </div>
      {notes.map((note) => (
        <p key={note} className="text-sm text-muted-foreground">
          {note}
        </p>
      ))}
      {fullAreaError !== null && (
        <p role="alert" className="text-sm text-destructive">
          {fullAreaError}
        </p>
      )}
    </div>
  );
};
